import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {MutationObserver | null} */
  #pendingAddObserver = null;

  /** @type {number | null} */
  #pendingAddTimeout = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('submit', this.#handleCartAddSubmit, true);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('click', this.#handleInstallmentsClick, true);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('submit', this.#handleCartAddSubmit, true);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
    this.#clearPendingAddWatcher();
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /** Handles the theme-drawer opening by updating its sticky state. */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
  };

  /**
   * Closes the cart drawer before the installments dialog opens.
   *
   * The payment-terms element replaces its shadow content when cart totals change,
   * so listen on this stable ancestor instead of binding to a particular CTA instance.
   *
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host.matches('shopify-payment-terms')) return;

    this.#themeDrawer?.close();
  };

  /**
   * Open the drawer on the native product form submit path, before the cart
   * section response returns. This keeps the add-to-cart action feeling instant
   * even when Shopify section rendering is slow.
   *
   * @param {SubmitEvent} event
   */
  #handleCartAddSubmit = (event) => {
    if (!this.hasAttribute('auto-open') || this.#themeDrawer?.isOpen) return;
    if (!(event.target instanceof HTMLFormElement)) return;
    if (!this.#isCartAddForm(event.target)) return;

    const sourceModal = event.target.closest('dialog:modal');
    if (sourceModal) return;

    this.#showPendingAdd();
    this.#themeDrawer?.open();
    this.#hidePendingAddWhenFilled();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal) {
      this.#showPendingAdd();
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          this.#hidePendingAdd();
          settle();
          return;
        }

        const openAndSettle = () => {
          this.#showPendingAdd();
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          this.#hidePendingAddWhenFilled();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        this.#hidePendingAdd();
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * @param {HTMLFormElement} form
   */
  #isCartAddForm(form) {
    return form.action.includes('/cart/add');
  }

  #showPendingAdd() {
    const isEmpty = this.#isCartEmpty();
    this.setAttribute('data-pending-add', 'true');
    this.toggleAttribute('data-pending-empty', isEmpty);

    const existingPending = this.querySelector('.cart-drawer__pending-add');
    if (existingPending) {
      existingPending.classList.toggle('cart-drawer__pending-add--empty', isEmpty);
      existingPending.classList.toggle('cart-drawer__pending-add--inline', !isEmpty);
      return;
    }

    const pending = document.createElement('div');
    pending.className = `cart-drawer__pending-add cart-drawer__pending-add--${isEmpty ? 'empty' : 'inline'}`;
    pending.setAttribute('role', 'status');
    pending.setAttribute('aria-live', 'polite');
    pending.textContent = 'Adding to cart...';
    this.prepend(pending);
  }

  #hidePendingAdd() {
    this.#clearPendingAddWatcher();
    this.removeAttribute('data-pending-add');
    this.removeAttribute('data-pending-empty');
    this.querySelector('.cart-drawer__pending-add')?.remove();
  }

  #hidePendingAddWhenFilled() {
    this.#clearPendingAddWatcher();

    if (!this.#isCartEmpty()) {
      this.#pendingAddTimeout = window.setTimeout(() => this.#hidePendingAdd(), 120);
      return;
    }

    this.#pendingAddObserver = new MutationObserver(() => {
      if (this.#isCartEmpty()) return;
      this.#hidePendingAdd();
    });

    this.#pendingAddObserver.observe(this, { childList: true, subtree: true });
    this.#pendingAddTimeout = window.setTimeout(() => {
      this.#hidePendingAdd();
    }, 6000);
  }

  setFilledState() {
    const inner = this.querySelector('.cart-drawer__inner');
    inner?.classList.remove('cart-drawer--empty');
    inner?.removeAttribute('hidden');
    this.#hidePendingAdd();
    requestAnimationFrame(() => this.#updateStickyState());
  }

  #clearPendingAddWatcher() {
    this.#pendingAddObserver?.disconnect();
    this.#pendingAddObserver = null;

    if (this.#pendingAddTimeout) {
      window.clearTimeout(this.#pendingAddTimeout);
      this.#pendingAddTimeout = null;
    }
  }

  #updateStickyState() {
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.querySelectorAll('[data-gc-deferred-cart-account]').forEach((holder) => {
        const template = holder.querySelector('template[data-gc-cart-account-template]');
        if (template instanceof HTMLTemplateElement) holder.replaceChildren(template.content.cloneNode(true));
      });
    }
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
